import { ParsedCSVData, ValidationError } from '@/types/upload';
import {
  CouponDiscountTypeSchema,
  CouponGetTypeSchema,
  CouponUseTypeSchema,
} from '@/types/api.zod';
import { type CsvRow, toCsvRow } from '@/utils/csvRow';

/**
 * クーポン CSV のクライアント側バリデーション。
 *
 * S3 へ presigned URL でアップロードする前に、Book / Product / Menu / Corner / Special と同様に
 * 全ルールをクライアントで検証してプレビュー可否を判定する。サーバー（ProcessUpload Lambda /
 * CouponUploadService）でも同一ルールを再検証する（多層防御）。
 *
 * Corner / Special と違い **1 行 = 1 クーポン**のフラット形式で、data_type による階層を持たない。
 *
 * 仕様の正本: docs/features/coupon.md「CSV フォーマット」「CSV バリデーションルール」。
 * get_type / use_type / discount_type の許容値は codegen の Zod enum（単一の出所）から導出する。
 *
 * 権威はサーバー（coupon_service.py）側にあり、ここはアップロード前の UX 向け事前検証。
 * 日時 / 期限の排他 / 割引値の範囲等のルールはバックエンドと手動ミラーのため、
 * 仕様変更時は docs/features/coupon.md を基点に coupon_service.py と本ファイルの両方を更新すること。
 *
 * 公開状態（status）は列に持たない。配布停止は同一 coupon_id で end_at を過去日時に変更した CSV を
 * 再アップロードして行う（docs/features/coupon.md「持たない項目」）。
 *
 * バリデーション 8「use_list の値が実在すること」はサーバー側でも未実装のため、ここでも
 * 実在確認は行わない。代替として use_list の値は**書式**を検証し、列の入れ違いを検出する
 * （product は JDCN の半角英数 20 桁 / group は半角英数と `-` `_`。バリデーション 17）。
 */

const COLUMNS = [
  'coupon_id',
  'title',
  'coupon_description',
  'start_at',
  'end_at',
  'get_type',
  'get_from_list',
  'use_type',
  'use_list',
  'discount_type',
  'value',
  'usable_period',
  'usable_start_at',
  'usable_end_at',
  'usable_count',
  'is_combine_usable',
  'buy_now_only',
  'buy_cart_only',
] as const;

// 列名をキーに持つ 1 行分の型（値は常に string）。詳細は utils/csvRow.ts。
type CouponColumn = (typeof COLUMNS)[number];
type CouponRow = CsvRow<typeof COLUMNS>;

// 必須列（docs/features/coupon.md 列定義の ○ 印。バリデーション 14）。
// △（条件付き必須）の get_from_list / use_list は個別ルールで検証する。
const REQUIRED_COLUMNS: readonly CouponColumn[] = [
  'coupon_id',
  'title',
  'coupon_description',
  'start_at',
  'get_type',
  'use_type',
  'discount_type',
  'value',
  'usable_count',
];

// 0 / 1 のフラグ列（空欄可）。get_from_list は条件付き必須のため個別に検証する。
const FLAG_COLUMNS: readonly CouponColumn[] = [
  'is_combine_usable',
  'buy_now_only',
  'buy_cart_only',
];

// 日時列（すべて JST として解釈する。docs/features/coupon.md「CSV フォーマット」）。
const DATETIME_COLUMNS: readonly CouponColumn[] = [
  'start_at',
  'end_at',
  'usable_start_at',
  'usable_end_at',
];

// 半角英数 + `-` / `_` の 32 桁（バリデーション 11）。区切りに使う `#` を含まないため
// `COUPON#<coupon_id>` などの DynamoDB キーへそのまま埋め込める。
const COUPON_ID_RE = /^[a-zA-Z0-9_-]{32}$/;
const JDCN_RE = /^[a-zA-Z0-9]{20}$/; // use_type=product の書式検証
// use_type=group の値の書式（commons の AlphanumericSlug と同一）。実在確認
// （バリデーション 8）の代替ではなく、スコープ逆引きの PK（SCOPE#GROUP#<value>）へそのまま入る値の
// 最低限のガード。無検証だと列ズレで SCOPE#GROUP#percent のようなキーが黙って作られる。
const SCOPE_VALUE_RE = /^[a-zA-Z0-9_-]+$/;
const FLAG_RE = /^[01]$/;
const INTEGER_RE = /^-?\d+$/;
// CSV 日時（`YYYY/M/D H:MM`・JST）。commons の CsvDateTime（codegen）と同じ範囲を受理する:
// 年月日と時は `/` `-` 区切り・ゼロパディング省略可だが、**分と秒は 2 桁固定**。
// 時刻の省略とタイムゾーン表記は認めない。
// ⚠ coupon_service.py の _CSV_DATETIME_PATTERN と完全に一致させること。緩いとサーバーだけが
// 弾く形になり（例 `2026/8/1 10:5`）、事前検証を通ったのに全行エラーになる。
const CSV_DATETIME_RE = /^(\d{4})[/-](\d{1,2})[/-](\d{1,2}) (\d{1,2}):(\d{2})(?::(\d{2}))?$/;
const DATETIME_FORMAT_HINT = '（例: 2026/8/1 10:00・JST。日付のみ / タイムゾーン付きは不可）';

const PERCENT_VALUE_MIN = 1;
const PERCENT_VALUE_MAX = 100; // discount_type=percent の上限（バリデーション 4）
const FIXED_VALUE_MIN = 1; // discount_type=fixed の下限（バリデーション 12）
const UNLIMITED_USABLE_COUNT = -1; // usable_count=-1 は無制限（バリデーション 6）

const GET_TYPES: readonly string[] = CouponGetTypeSchema.options;
const USE_TYPES: readonly string[] = CouponUseTypeSchema.options;
const DISCOUNT_TYPES: readonly string[] = CouponDiscountTypeSchema.options;

// use_list に値を持つ use_type（all は空欄）
const SCOPED_USE_TYPES: readonly string[] = USE_TYPES.filter((t) => t !== 'all');

const err = (row: number, column: string, value: string, error: string): ValidationError => ({
  row,
  column,
  value,
  error,
});

// 存在しない日付（例: 2026-02-30 / 2026-06-31）を UTC ロールオーバーの有無で弾く
const isRealDate = (year: number, month: number, day: number): boolean => {
  const probe = new Date(Date.UTC(year, month - 1, day));
  return (
    probe.getUTCFullYear() === year &&
    probe.getUTCMonth() === month - 1 &&
    probe.getUTCDate() === day
  );
};

type DateTimeParts = {
  year: number;
  month: number;
  day: number;
  hour: number;
  minute: number;
  second: number;
};

/**
 * CSV 日時を分解する。書式不正・時刻範囲外・存在しない日付は null。
 *
 * 形式判定と epoch 変換の両方をこの 1 箇所に集約している（epoch 変換側で「パース失敗」を
 * 再度扱わずに済み、到達不能な分岐を作らない）。
 */
const parseDateTime = (value: string): DateTimeParts | null => {
  const matched = CSV_DATETIME_RE.exec(value.trim());
  if (!matched) return null;
  const [, y, mo, d, h, mi, sec] = matched;
  const parts: DateTimeParts = {
    year: Number(y),
    month: Number(mo),
    day: Number(d),
    hour: Number(h),
    minute: Number(mi),
    second: Number(sec ?? 0),
  };
  if (parts.hour > 23 || parts.minute > 59 || parts.second > 59) return null;
  if (!isRealDate(parts.year, parts.month, parts.day)) return null;
  return parts;
};

const isValidDateTime = (value: string): boolean => parseDateTime(value) !== null;

// CSV のタイムゾーン無し日時は JST（UTC+9）とみなす（サーバ DateTimeUtils と一致）。
const JST_OFFSET_MS = 9 * 60 * 60 * 1000;

// 大小関係の判定用。壁時計時刻を Date.UTC で組み立てて JST オフセットを引く
// （specialCsvValidation と同一方式）。
const toEpoch = (p: DateTimeParts): number =>
  Date.UTC(p.year, p.month - 1, p.day, p.hour, p.minute, p.second) - JST_OFFSET_MS;

/** 整数列をパースする。空欄・非整数は null（必須と形式のエラーは呼び出し側で出す） */
const parseInteger = (
  rowNum: number,
  column: CouponColumn,
  value: string,
  errors: ValidationError[]
): number | null => {
  if (!value) return null;
  if (!INTEGER_RE.test(value)) {
    errors.push(err(rowNum, column, value, `${column} は整数で指定してください`));
    return null;
  }
  return Number(value);
};

const validateRequired = (rowNum: number, d: CouponRow, errors: ValidationError[]): void => {
  REQUIRED_COLUMNS.forEach((col) => {
    if (!d[col]) errors.push(err(rowNum, col, d[col], `${col} は必須です`));
  });
};

const validateEnums = (rowNum: number, d: CouponRow, errors: ValidationError[]): void => {
  if (d.get_type && !GET_TYPES.includes(d.get_type)) {
    errors.push(
      err(rowNum, 'get_type', d.get_type, `get_type は ${GET_TYPES.join(' / ')} のいずれかです`)
    );
  }
  if (d.use_type && !USE_TYPES.includes(d.use_type)) {
    errors.push(
      err(rowNum, 'use_type', d.use_type, `use_type は ${USE_TYPES.join(' / ')} のいずれかです`)
    );
  }
  if (d.discount_type && !DISCOUNT_TYPES.includes(d.discount_type)) {
    errors.push(
      err(
        rowNum,
        'discount_type',
        d.discount_type,
        `discount_type は ${DISCOUNT_TYPES.join(' / ')} のいずれかです`
      )
    );
  }
};

const validateDateTimes = (rowNum: number, d: CouponRow, errors: ValidationError[]): void => {
  DATETIME_COLUMNS.forEach((col) => {
    if (d[col] && !isValidDateTime(d[col])) {
      errors.push(err(rowNum, col, d[col], `${col} の日時形式が不正です${DATETIME_FORMAT_HINT}`));
    }
  });
};

/** 両端が揃っているときだけ大小関係を検証（バリデーション 7。片方空欄は無期限として有効） */
const validatePeriodOrder = (
  rowNum: number,
  d: CouponRow,
  startCol: CouponColumn,
  endCol: CouponColumn,
  errors: ValidationError[]
): void => {
  const start = parseDateTime(d[startCol]);
  const end = parseDateTime(d[endCol]);
  if (start === null || end === null) return; // 空欄・形式不正はここでは扱わない
  if (toEpoch(start) >= toEpoch(end)) {
    errors.push(
      err(rowNum, endCol, d[endCol], `${startCol} は ${endCol} より前の日時で指定してください`)
    );
  }
};

/** バリデーション 5 / 15: get_from_list の条件付き必須と auto_grant での掲載禁止 */
const validateGetFromList = (rowNum: number, d: CouponRow, errors: ValidationError[]): void => {
  if (d.get_type === 'user_claim' && !d.get_from_list) {
    errors.push(
      err(
        rowNum,
        'get_from_list',
        '',
        'get_type=user_claim の場合 get_from_list は必須です（0 / 1）'
      )
    );
  }
  if (d.get_from_list && !FLAG_RE.test(d.get_from_list)) {
    errors.push(
      err(rowNum, 'get_from_list', d.get_from_list, 'get_from_list は 0 / 1 で指定してください')
    );
  } else if (d.get_type === 'auto_grant' && d.get_from_list === '1') {
    errors.push(
      err(
        rowNum,
        'get_from_list',
        d.get_from_list,
        'get_type=auto_grant の場合 get_from_list に 1 は指定できません（一覧からは取得できません）'
      )
    );
  }
};

/** バリデーション 2 / 16 / 17: use_type と use_list の整合（条件付き必須・all は空欄・重複と書式） */
const validateUseList = (rowNum: number, d: CouponRow, errors: ValidationError[]): void => {
  const values = d.use_list
    .split(',')
    .map((v) => v.trim())
    .filter((v) => v !== '');

  if (d.use_type === 'all') {
    if (d.use_list) {
      errors.push(
        err(rowNum, 'use_list', d.use_list, 'use_type=all の場合 use_list は空欄にしてください')
      );
    }
    return;
  }

  if (!SCOPED_USE_TYPES.includes(d.use_type)) return; // use_type 不正は validateEnums 済み

  if (values.length === 0) {
    errors.push(
      err(rowNum, 'use_list', d.use_list, `use_type=${d.use_type} の場合 use_list は必須です`)
    );
    return;
  }

  const duplicated = [
    ...new Set(values.filter((v) => values.indexOf(v) !== values.lastIndexOf(v))),
  ];
  if (duplicated.length > 0) {
    errors.push(
      err(
        rowNum,
        'use_list',
        d.use_list,
        `use_list に重複した値があります: ${duplicated.join(' / ')}`
      )
    );
  }

  // 書式検証（列の入れ違い検出用。実在確認は行わない）。product は JDCN（半角英数 20 桁）、
  // group は英数 + `-` / `_`（commons の AlphanumericSlug 相当）。
  if (d.use_type === 'product') {
    const invalid = values.filter((v) => !JDCN_RE.test(v));
    if (invalid.length > 0) {
      errors.push(
        err(
          rowNum,
          'use_list',
          d.use_list,
          `use_type=product の use_list は JDCN（半角英数 20 桁）です: ${invalid.join(' / ')}`
        )
      );
    }
  } else {
    const invalid = values.filter((v) => !SCOPE_VALUE_RE.test(v));
    if (invalid.length > 0) {
      errors.push(
        err(
          rowNum,
          'use_list',
          d.use_list,
          `use_type=${d.use_type} の use_list は半角英数・ハイフン（-）・アンダースコア（_）のみ使用できます: ${invalid.join(' / ')}`
        )
      );
    }
  }
};

/** バリデーション 3: 相対期限（usable_period）と絶対期限（usable_start_at / usable_end_at）の排他 */
const validateUseablePeriod = (rowNum: number, d: CouponRow, errors: ValidationError[]): void => {
  const period = parseInteger(rowNum, 'usable_period', d.usable_period, errors);
  if (period !== null && period < 1) {
    errors.push(
      err(
        rowNum,
        'usable_period',
        d.usable_period,
        'usable_period は 1 以上の整数で指定してください'
      )
    );
  }

  const absolute = (['usable_start_at', 'usable_end_at'] as const).filter((col) => d[col]);
  if (d.usable_period && absolute.length > 0) {
    errors.push(
      err(
        rowNum,
        'usable_period',
        d.usable_period,
        `usable_period と ${absolute.join(' / ')} は同時に指定できません（相対期限と絶対期限の併用不可）`
      )
    );
  }
};

/** バリデーション 4 / 12: discount_type 別の value 範囲 */
const validateValue = (rowNum: number, d: CouponRow, errors: ValidationError[]): void => {
  const value = parseInteger(rowNum, 'value', d.value, errors);
  if (value === null) return;
  if (d.discount_type === 'percent' && (value < PERCENT_VALUE_MIN || value > PERCENT_VALUE_MAX)) {
    errors.push(
      err(
        rowNum,
        'value',
        d.value,
        `discount_type=percent の value は ${PERCENT_VALUE_MIN}〜${PERCENT_VALUE_MAX} で指定してください`
      )
    );
  } else if (d.discount_type === 'fixed' && value < FIXED_VALUE_MIN) {
    errors.push(
      err(
        rowNum,
        'value',
        d.value,
        `discount_type=fixed の value は ${FIXED_VALUE_MIN} 以上で指定してください`
      )
    );
  }
};

/** バリデーション 6 / 9 / フラグ列の書式 */
const validateCountAndFlags = (rowNum: number, d: CouponRow, errors: ValidationError[]): void => {
  const count = parseInteger(rowNum, 'usable_count', d.usable_count, errors);
  if (count !== null && count !== UNLIMITED_USABLE_COUNT && count < 1) {
    errors.push(
      err(
        rowNum,
        'usable_count',
        d.usable_count,
        'usable_count は 1 以上の整数、または -1（無制限）で指定してください'
      )
    );
  }

  FLAG_COLUMNS.forEach((col) => {
    if (d[col] && !FLAG_RE.test(d[col])) {
      errors.push(err(rowNum, col, d[col], `${col} は 0 / 1 で指定してください`));
    }
  });
  if (d.buy_now_only === '1' && d.buy_cart_only === '1') {
    errors.push(
      err(
        rowNum,
        'buy_cart_only',
        d.buy_cart_only,
        'buy_now_only と buy_cart_only の両方に 1 は指定できません'
      )
    );
  }
};

export const validateCouponCsv = (data: ParsedCSVData): ValidationError[] => {
  const errors: ValidationError[] = [];

  // ヘッダー（順序込み完全一致）
  const headerMatches =
    data.headers.length === COLUMNS.length && COLUMNS.every((c, i) => data.headers[i] === c);
  if (!headerMatches) {
    return [
      err(
        1,
        'ヘッダー',
        data.headers.join(','),
        `CSVヘッダーが不正です。期待: ${COLUMNS.join(',')}`
      ),
    ];
  }

  if (data.rows.length === 0) {
    return [
      err(1, 'データ', '', 'データ行がありません（1=カラム名 / 2=定義名 / 3 行目以降=データ）'),
    ];
  }

  // parseCSV が 2 行目（定義名）をスキップ済みのため data.rows[0] は実ファイル 3 行目に相当する。
  const seenCouponIds = new Map<string, number>();

  data.rows.forEach((row, idx) => {
    const rowNum = idx + 3;

    if (row.length !== COLUMNS.length) {
      errors.push(
        err(
          rowNum,
          '列数',
          row.join(','),
          `列数が不正です（${row.length} 列、期待 ${COLUMNS.length} 列）`
        )
      );
      return;
    }

    const d = toCsvRow(COLUMNS, row);

    validateRequired(rowNum, d, errors);
    // title / coupon_description に最大長は設けない（全文表示のため。
    // docs/features/coupon.md 列定義 2 / 3 と「残課題への回答」）。
    if (d.coupon_id && !COUPON_ID_RE.test(d.coupon_id)) {
      errors.push(
        err(
          rowNum,
          'coupon_id',
          d.coupon_id,
          'coupon_id は半角英数・ハイフン（-）・アンダースコア（_）の 32 桁で指定してください'
        )
      );
    }
    validateDateTimes(rowNum, d, errors);
    validateEnums(rowNum, d, errors);
    validateGetFromList(rowNum, d, errors);
    validateUseList(rowNum, d, errors);
    validateValue(rowNum, d, errors);
    validateCountAndFlags(rowNum, d, errors);
    validateUseablePeriod(rowNum, d, errors);
    validatePeriodOrder(rowNum, d, 'start_at', 'end_at', errors);
    validatePeriodOrder(rowNum, d, 'usable_start_at', 'usable_end_at', errors);

    // バリデーション 1: coupon_id の重複（後の行が前の行を上書きするため追跡不能になる）
    if (d.coupon_id) {
      const firstRow = seenCouponIds.get(d.coupon_id);
      if (firstRow !== undefined) {
        errors.push(
          err(
            rowNum,
            'coupon_id',
            d.coupon_id,
            `coupon_id が重複しています: '${d.coupon_id}'（${firstRow} 行目と同一）`
          )
        );
      } else {
        seenCouponIds.set(d.coupon_id, rowNum);
      }
    }
  });

  return errors;
};
